import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { API, SITE, SERVICE_OPTIONS } from "@/lib/site";

const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

const INFO = [
  { icon: Phone, label: "Call or Text", value: SITE.phone, href: SITE.phoneHref, testid: "contact-info-phone" },
  { icon: Mail, label: "Email", value: SITE.email, href: SITE.emailHref, testid: "contact-info-email" },
  { icon: MapPin, label: "Service Area", value: SITE.area, testid: "contact-info-area" },
  { icon: Clock, label: "Hours", value: `${SITE.hours} · ${SITE.hoursNote}`, testid: "contact-info-hours" },
];

const inputCls =
  "w-full bg-[#0c0e12] border border-white/15 focus:border-orange-500 focus:outline-none text-slate-100 text-sm px-4 py-3.5 placeholder:text-slate-600 transition-colors duration-200";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — we'll get back to you within one business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your enquiry. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-36 px-6 lg:px-12 bg-[#0a0c10]" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.4em] text-orange-500 mb-4">Get a quote</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-white uppercase">
            Request a Free <span className="text-orange-500">Estimate</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="flex flex-col gap-4">
              {INFO.map(({ icon: Icon, label, value, href, testid }) => (
                <div key={label} className="flex items-start gap-4 bg-[#14171f] border border-white/10 p-5" data-testid={testid}>
                  <span className="bg-orange-600/15 border border-orange-600/30 p-2.5 text-orange-500 shrink-0">
                    <Icon size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{label}</p>
                    {href ? (
                      <a href={href} className="mt-1 block text-sm text-slate-100 hover:text-orange-500 transition-colors break-all">
                        {value}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm text-slate-100">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} className="bg-[#14171f] border border-white/10 p-6 lg:p-10" data-testid="enquiry-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="input-name" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Full Name *</label>
                  <input id="input-name" data-testid="input-name" required value={form.name} onChange={set("name")} placeholder="John Miller" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="input-email" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Email Address *</label>
                  <input id="input-email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} placeholder="john@example.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="input-phone" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Phone Number *</label>
                  <input id="input-phone" data-testid="input-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="(812) 555-0100" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="select-service" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Service Required *</label>
                  <select id="select-service" data-testid="select-service" required value={form.service} onChange={set("service")} className={inputCls}>
                    <option value="" disabled>Select a service</option>
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="textarea-message" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Project Scope & Details *</label>
                  <textarea id="textarea-message" data-testid="textarea-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us what you need built, fixed, or installed..." className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="btn-submit-enquiry"
                disabled={sending}
                className="mt-8 inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-[#0c0e12] font-mono text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 transition-all duration-200 hover:-translate-y-0.5"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2.5} />}
                {sending ? "Sending..." : "Submit Service Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
